/**
 * Button — primitivo de botón del rediseño.
 *
 * Canónico para acciones. Compone sus clases (no usa `.btn-*` de index.css) para
 * que tamaños y variantes combinen limpio; el look coincide con esas clases + la
 * paleta brand/surface. Los `.btn-*` legacy siguen válidos hasta migrar cada uso.
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import clsx from 'clsx'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
type Size = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  /** Ocupa todo el ancho disponible. */
  block?: boolean
}

const BASE =
  'inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 dark:focus-visible:ring-offset-card ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

// Variantes según §5.1 del sistema de diseño. Todo por token: el primario es
// relleno de marca; el secundario, superficie con borde; el destructivo es
// *outline* (rojo solo en borde y texto, jamás de relleno fuera de un diálogo).
const VARIANT: Record<Variant, string> = {
  primary: 'bg-brand hover:bg-brand-hover text-brand-fg',
  secondary: 'bg-card border border-border hover:bg-card-2 text-fg',
  ghost: 'bg-transparent hover:bg-card-2 text-fg-2',
  danger: 'bg-card border border-err-line hover:bg-err-bg text-err-fg',
  success: 'bg-ok-bg border border-ok-line hover:brightness-95 text-ok-fg',
}

const SIZE: Record<Size, string> = {
  sm: 'h-9 px-3 text-[13px]',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-[15px]',
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', block, type = 'button', className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(BASE, VARIANT[variant], SIZE[size], block && 'w-full', className)}
      {...rest}
    />
  )
})

export default Button
